import React, { useRef, useState } from "react";
import { TWclassNames } from "./Div";
import { WorkJournalRichEditor } from "../dashboard/todos/WorkJournalRichEditor";
import "./PromangRichTextEditor.css";
import { DEFAULT_PROMANG_RICH_TEXT_CONTEXT_MENU } from "./richTextEditorContextMenuDefaults";
import { TemplateDialog } from "../dashboard/templates/template-dialog";

export { DEFAULT_PROMANG_RICH_TEXT_CONTEXT_MENU };

/**
 * Shared Quill bubble rich text (HTML) editor: formatting, links, embedded images (paste/upload).
 * Same core as work journal + mail reply drafts; use {@link size} for layout context.
 *
 * @param {object} props
 * @param {string} [props.value] — controlled HTML; after async saves, keep this in sync
 *   with the latest in-editor draft. Pushing only the snapshot that was just persisted
 *   while the user keeps typing can reset Quill (controlled `value` overwrites the editor).
 * @param {(html: string) => void} props.onChange
 * @param {string} [props.placeholder]
 * @param {boolean} [props.disabled]
 * @param {'sm'|'md'|'lg'} [props.size='md'] — vertical space for editor chrome
 * @param {boolean} [props.framed=true] — bordered panel (e.g. settings); false for embedded/dialog use
 * @param {boolean} [props.fullscreen]
 * @param {string} [props.className]
 * @param {string} [props.wrapperClassName] — extra classes on outer div
 * @param {boolean} [props.templatesEnabled=true] — show template picker and insert selected template HTML.
 * @param {boolean} [props.tablesEnabled=true] — show a small action for inserting editable tables.
 * @param {false | object} [props.contextMenu] — `false` = browser default menu only.
 *   Object merges with {@link DEFAULT_PROMANG_RICH_TEXT_CONTEXT_MENU} and may include:
 *   `insertImage`, `moveSelectionToPage`, `createPageWithSelection`,
 *   `onMoveSelectionToPage`, `onCreatePageWithSelection`
 *   (callbacks receive `{ quill, range, html, text, hasSelection }` for Notion-style “move to page” flows).
 */
const SIZE_CLASSES = {
  sm: "min-h-[140px] [&_.ql-container]:min-h-[112px] [&_.ql-editor]:min-h-[96px]",
  md: "min-h-[200px] [&_.ql-container]:min-h-[168px] [&_.ql-editor]:min-h-[152px]",
  lg: "min-h-[280px] [&_.ql-container]:min-h-[240px] [&_.ql-editor]:min-h-[220px]",
};

export function PromangRichTextEditor({
  value,
  onChange,
  placeholder,
  disabled,
  size = "md",
  framed = true,
  fullscreen,
  className,
  wrapperClassName,
  templatesEnabled = true,
  templateButtonLabel = "Velg mal",
  tablesEnabled = true,
  contextMenu,
}) {
  const sizeClass = SIZE_CLASSES[size] || SIZE_CLASSES.md;
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const wrapperRef = useRef(null);

  const handleTemplateSelect = (template, action = {}) => {
    const currentValue = value || "";
    const templateContent = template?.content || "";
    const shouldReplace = action.mode === "replace" || !currentValue;
    const nextValue = shouldReplace
      ? templateContent
      : `${currentValue}<p><br></p>${templateContent}`;

    onChange?.(nextValue);
    setTemplateDialogOpen(false);
  };

  return (
    <>
      <div
        ref={wrapperRef}
        className={TWclassNames(
          "promang-rich-text-editor",
          framed && "rounded-lg border border-gray-200 bg-white",
          disabled && "pointer-events-none opacity-60",
          wrapperClassName
        )}
        onMouseDown={(event) => {
          if (event.target.closest("button, input, select, textarea, a")) return;
          wrapperRef.current?.querySelector(".ql-editor")?.focus();
        }}
      >
        {templatesEnabled && !disabled && (
          <div className="flex justify-end border-b border-gray-100 px-3 py-2">
            <button
              type="button"
              onClick={() => setTemplateDialogOpen(true)}
              className="inline-flex items-center rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-slate-400 hover:bg-slate-50"
            >
              {templateButtonLabel}
            </button>
          </div>
        )}
        <WorkJournalRichEditor
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          fullscreen={fullscreen}
          className={TWclassNames("work-journal-quill--promang-mail", sizeClass, className)}
          contextMenu={contextMenu}
          tablesEnabled={tablesEnabled}
        />
      </div>
      {templatesEnabled && (
        <TemplateDialog
          open={templateDialogOpen}
          onClose={() => setTemplateDialogOpen(false)}
          hasExistingContent={Boolean((value || "").trim())}
          onChange={handleTemplateSelect}
        />
      )}
    </>
  );
}
